/**
 * v7.29 — Painel de Monitoramento Z-API (Admin).
 *
 * Mostra ao vivo (auto-refresh 15s):
 *   - Total de webhooks recebidos nas últimas 24h
 *   - Breakdown por tipo (on-message-received / on-message-status / etc)
 *   - Mini-gráfico de barras por hora
 *   - Sessões de chatbot ativas + pedidos pendentes no inbox
 *   - Último evento recebido (com timestamp relativo)
 *   - Tabela dos últimos 10 webhooks (kind, fone, fromMe, hora)
 *
 * Sem dependência externa de chart (CSS puro).
 */
import { useEffect, useMemo, useState, useCallback } from 'react';
import { Activity, RefreshCw, Webhook, MessageCircle, Inbox, ChevronLeft, Clock, AlertTriangle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const REFRESH_MS = 15_000;

function timeAgo(iso) {
  if (!iso) return '—';
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 0) return 'agora';
  const sec = Math.floor(ms / 1000);
  if (sec < 60) return `há ${sec}s`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `há ${min}min`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `há ${hr}h`;
  return `há ${Math.floor(hr / 24)}d`;
}

const KIND_LABEL = {
  'on-message-received': 'Mensagem recebida',
  'on-message-status': 'Status enviada',
  'on-connection': 'Conexão',
  'on-disconnected': 'Desconectado',
  'on-presence-changed': 'Presença',
};

const KIND_COLOR = {
  'on-message-received': 'bg-emerald-500',
  'on-message-status': 'bg-blue-500',
  'on-connection': 'bg-amber-500',
  'on-disconnected': 'bg-red-500',
  'on-presence-changed': 'bg-purple-500',
};

export default function ZapiMonitorPage() {
  const [stats, setStats] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastFetch, setLastFetch] = useState(null);
  const [error, setError] = useState('');

  const fetchAll = useCallback(async () => {
    try {
      const [s, e] = await Promise.all([
        fetch(`${API_URL}/api/webhooks/zapi/stats?hours=24`).then((r) => r.json()),
        fetch(`${API_URL}/api/webhooks/zapi/events?limit=15`).then((r) => r.json()),
      ]);
      if (s.ok === false) throw new Error(s.error || 'stats failed');
      setStats(s);
      setEvents(e?.events || []);
      setLastFetch(new Date());
      setError('');
    } catch (err) {
      setError(err?.message || String(err));
      // eslint-disable-next-line no-console
      console.warn('[ZapiMonitor] fetch falhou:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
    const iv = setInterval(fetchAll, REFRESH_MS);
    return () => clearInterval(iv);
  }, [fetchAll]);

  const maxHour = useMemo(() => {
    if (!stats?.timeline?.length) return 1;
    return Math.max(1, ...stats.timeline.map((t) => t.count));
  }, [stats]);

  // Preenche horas vazias para mostrar timeline contínua (24 buckets)
  const timeline = useMemo(() => {
    if (!stats?.timeline) return [];
    const map = new Map(stats.timeline.map((t) => [t.hour, t.count]));
    const out = [];
    const now = new Date();
    for (let i = 23; i >= 0; i--) {
      const d = new Date(now.getTime() - i * 60 * 60 * 1000);
      const key = d.toISOString().slice(0, 13); // YYYY-MM-DDTHH
      out.push({
        hour: key,
        label: String(d.getUTCHours()).padStart(2, '0'),
        count: map.get(key) || 0,
      });
    }
    return out;
  }, [stats]);

  const handleManualRefresh = () => {
    setLoading(true);
    fetchAll().then(() => toast.success('Atualizado'));
  };

  return (
    <div data-testid="zapi-monitor-page" className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link
              to="/admin"
              data-testid="back-to-admin"
              className="p-2 -ml-2 rounded-lg hover:bg-slate-100 text-slate-600"
            >
              <ChevronLeft size={20} />
            </Link>
            <div>
              <h1 className="font-heading text-lg font-black text-slate-900 flex items-center gap-2">
                <Activity size={18} className="text-emerald-600" />
                Monitor Z-API
              </h1>
              <p className="text-[11px] text-slate-500">
                Webhooks recebidos • atualiza a cada 15s
              </p>
            </div>
          </div>
          <button
            data-testid="manual-refresh-btn"
            onClick={handleManualRefresh}
            disabled={loading}
            className="inline-flex items-center gap-2 h-9 px-3 rounded-lg border-2 border-slate-200 text-slate-600 text-sm font-bold hover:bg-slate-50 disabled:opacity-50"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            Atualizar
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto p-4 space-y-4">
        {error && (
          <div data-testid="monitor-error" className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-800">
            <AlertTriangle size={16} />
            <span><strong>Falha ao buscar:</strong> {error}</span>
          </div>
        )}

        {/* Cards de métricas */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard
            testid="metric-total"
            icon={<Webhook size={16} />}
            label="Webhooks/24h"
            value={stats?.total ?? '—'}
            color="bg-emerald-50 border-emerald-200 text-emerald-700"
          />
          <StatCard
            testid="metric-sessions"
            icon={<MessageCircle size={16} />}
            label="Conversas ativas"
            value={stats?.chatbot?.activeSessions ?? '—'}
            color="bg-cyan-50 border-cyan-200 text-cyan-700"
          />
          <StatCard
            testid="metric-pending"
            icon={<Inbox size={16} />}
            label="Pedidos pendentes"
            value={stats?.chatbot?.pendingInbox ?? '—'}
            color="bg-amber-50 border-amber-200 text-amber-700"
          />
          <StatCard
            testid="metric-last-event"
            icon={<Clock size={16} />}
            label="Último webhook"
            value={timeAgo(stats?.lastEvent?.receivedAt)}
            color="bg-indigo-50 border-indigo-200 text-indigo-700"
          />
        </div>

        {/* Breakdown por tipo */}
        {stats?.byKind?.length > 0 && (
          <div data-testid="kind-breakdown" className="bg-white border border-slate-200 rounded-2xl p-4">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-700 mb-3">Por tipo de evento</p>
            <div className="space-y-2">
              {stats.byKind.map((k) => {
                const pct = (k.count / (stats.total || 1)) * 100;
                return (
                  <div key={k.kind} data-testid={`kind-row-${k.kind}`} className="flex items-center gap-3">
                    <div className="w-44 text-sm font-semibold text-slate-700 truncate">
                      {KIND_LABEL[k.kind] || k.kind}
                    </div>
                    <div className="flex-1 h-3 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full ${KIND_COLOR[k.kind] || 'bg-slate-400'} transition-all`}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <div className="w-12 text-right text-sm font-mono font-bold text-slate-900">{k.count}</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Timeline 24h */}
        <div data-testid="timeline-24h" className="bg-white border border-slate-200 rounded-2xl p-4">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-700">Distribuição últimas 24h</p>
            <p className="text-[10px] text-slate-400">UTC</p>
          </div>
          <div className="flex items-end gap-1 h-32">
            {timeline.map((t, i) => {
              const h = maxHour > 0 ? Math.max(2, (t.count / maxHour) * 100) : 2;
              const active = t.count > 0;
              return (
                <div
                  key={t.hour}
                  data-testid={`bar-${i}`}
                  title={`${t.label}h — ${t.count} webhook(s)`}
                  className="flex-1 flex flex-col items-center gap-1"
                >
                  <div
                    className={`w-full rounded-t ${active ? 'bg-emerald-500' : 'bg-slate-200'} transition-all relative group`}
                    style={{ height: `${h}%` }}
                  >
                    {active && (
                      <span className="absolute -top-5 left-1/2 -translate-x-1/2 text-[9px] font-bold text-slate-700 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                        {t.count}
                      </span>
                    )}
                  </div>
                  <span className="text-[9px] text-slate-400 font-mono">{t.label}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Tabela de eventos recentes */}
        <div data-testid="recent-events-table" className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
          <div className="p-4 border-b border-slate-100">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-700">Últimos eventos</p>
          </div>
          {events.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-400">
              Nenhum webhook recebido ainda.
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {events.map((ev, idx) => {
                const txt = ev?.payload?.text?.message
                  || ev?.payload?.audio && '[áudio]'
                  || ev?.payload?.image && '[imagem]'
                  || ev?.payload?.sticker && '[sticker]'
                  || ev?.payload?.status
                  || '';
                return (
                  <div
                    key={`${ev?.receivedAt || 'ev'}-${idx}`}
                    data-testid={`event-row-${idx}`}
                    className="px-4 py-2.5 hover:bg-slate-50 flex items-center gap-3 text-sm"
                  >
                    <span className={`inline-block w-2 h-2 rounded-full ${KIND_COLOR[ev.kind] || 'bg-slate-400'}`} />
                    <div className="w-40 truncate font-mono text-xs text-slate-500">{ev.phone || '—'}</div>
                    <div className="flex-1 text-slate-700 truncate">
                      <span className="font-semibold mr-1">{KIND_LABEL[ev.kind] || ev.kind}</span>
                      {txt && <span className="text-slate-500">— {String(txt).slice(0, 80)}</span>}
                    </div>
                    {ev.fromMe && (
                      <span className="text-[10px] font-bold uppercase tracking-wider bg-slate-200 text-slate-600 rounded-full px-2 py-0.5">enviada</span>
                    )}
                    <span className="text-[10px] text-slate-400 font-mono whitespace-nowrap">{timeAgo(ev.receivedAt)}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <p className="text-center text-[10px] text-slate-400">
          {lastFetch && `Atualizado às ${lastFetch.toLocaleTimeString('pt-BR')}`}
        </p>
      </main>
    </div>
  );
}

function StatCard({ testid, icon, label, value, color }) {
  return (
    <div data-testid={testid} className={`border rounded-2xl p-3 ${color}`}>
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-bold opacity-80">
        {icon}
        {label}
      </div>
      <div className="mt-1 text-2xl font-black font-mono">{value}</div>
    </div>
  );
}
